import * as React from 'react'
import * as Tooltip from 'react-tooltip'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { API, IPOSListing } from '@getgreenline/homi-shared'
import { ProductListingRow } from './ProductListingRow'
import { Skeleton } from 'antd'
import { getCategoryName } from '../../../../../utilities/helpers'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  isSyncLoading: boolean
  errorMessage?: string
  fullSyncErrorMessage?: string
  selectedCategoryId?: string
  listings?: IPOSListing[]
}

// Use to not overload the Wordpress servers
function sleep(milliseconds: number) {
  return new Promise((resolve) => {
    setTimeout(() => resolve(''), milliseconds)
  })
}

@inject('currentCompanyStore')
@observer
export class ProductListing extends React.Component<Props, State> {
  productRows: Array<ProductListingRow | null> = []

  constructor(props: Props) {
    super(props)
    this.state = {
      isSyncLoading: false,
    }
  }

  componentDidMount() {
    const company = this.props.currentCompanyStore!.company!
    this.props.currentCompanyStore!.productStore!.getCategories()
    this.loadListings()
  }

  loadListings() {
    const company = this.props.currentCompanyStore!.company!
    const categoryId = this.state.selectedCategoryId
    this.setState({
      listings: undefined,
    })
    if (company.wooCommerceLocationId && categoryId) {
      API.getPOSListings(company.id, company.wooCommerceLocationId, { categoryId: categoryId })
        .then((listings) => {
          this.setState({
            listings: listings,
          })
        })
        .catch((error) => {
          this.setState({
            errorMessage:
              error.response.data.message || 'There was an error fetching your products',
          })
        })
    } else {
      this.setState({
        listings: [],
      })
    }
  }

  async syncAllProducts() {
    if (
      window.confirm(
        'Inventory syncing is much faster - it is advised to do that instead if products are already synced. Please stay on this page until the sync is complete.',
      )
    ) {
      let counter = 0
      while (counter < this.productRows.length) {
        const productRow = this.productRows[counter]
        if (productRow) {
          try {
            await ((productRow as any).wrappedInstance as ProductListingRow).syncProduct()
            counter++
            await sleep(200)
          } catch (error) {
            console.error(error)
            await sleep(200)
          }
        } else {
          counter++
        }
      }
    }
  }

  async syncAllInventory() {
    let counter = 0
    while (counter < this.productRows.length) {
      const productRow = this.productRows[counter]
      if (productRow) {
        try {
          await ((productRow as any).wrappedInstance as ProductListingRow).syncInventory()
          counter++
          await sleep(200)
        } catch (error) {
          console.error(error)
          await sleep(200)
        }
      } else {
        counter++
      }
    }
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const nestedCategories = this.props.currentCompanyStore!.productStore!.categories || []
    const flattenedCategories =
      this.props.currentCompanyStore!.productStore!.flattenedCategories || []

    let productsSection: JSX.Element | undefined
    if (this.state.listings) {
      const listings = this.state.listings

      const flattenedListings: IPOSListing[] = []
      listings.forEach((listing) => {
        flattenedListings.push(listing)
        if (listing.childProducts) {
          listing.childProducts.forEach((childListing) => {
            flattenedListings.push(childListing)
          })
        }
      })

      productsSection = (
        <div>
          <table className='table table-sm'>
            <thead className='thead-light'>
              <tr>
                <th>SKU</th>
                <th>Product</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Sync status</th>
                <th>
                  <span className='mr-2'>Options</span>
                  <span className='fa fa-info-circle text-muted' data-tip data-for='sync-product' />
                  <Tooltip id='sync-product' place='right' effect='solid'>
                    <div style={{ fontWeight: 400 }}>
                      Syncing the product clear the existing product on WooCommerce and re-create
                      it.
                    </div>
                    <div style={{ fontWeight: 400 }}>
                      Syncing the inventory will leave product details alone and only update the
                      stock quantity.
                    </div>
                  </Tooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              {flattenedListings.map((listing, index) => {
                return (
                  <ProductListingRow
                    key={listing.id}
                    listing={listing}
                    onUpdate={(result) => {
                      listings.forEach((l) => {
                        if (l.id === listing.id) {
                          if (result.wooCommerceProductId) {
                            l.wooCommerceProductId = result.wooCommerceProductId
                          }
                          l.wooCommerceSyncDate = new Date(result.wooCommerceSyncDate)
                        }
                        l.childProducts.forEach((cp) => {
                          if (cp.id === listing.id) {
                            if (cp.wooCommerceProductId === result.wooCommerceProductId) {
                              cp.wooCommerceProductId = result.wooCommerceProductId
                            }
                            cp.wooCommerceSyncDate = new Date(result.wooCommerceSyncDate)
                          }
                        })
                      })
                      this.setState({ listings: listings })
                    }}
                    ref={(ref) => (this.productRows[index] = ref)}
                  />
                )
              })}
            </tbody>
          </table>
        </div>
      )
    } else {
      productsSection = (
        <div>
          <Skeleton active />
        </div>
      )
    }

    return (
      <div>
        {(!company.wooCommerceLocationId ||
          !company.wooCommerceKey ||
          !company.wooCommerceSecret ||
          !company.wooCommerceLocationId ||
          !company.wooCommerceUrl) && (
          <div className='alert alert-danger'>
            <span className='fa fa-exclamation mr-2' />
            Your WooCommerce connection is not yet complete
            <br />
            Please enter your WooCommerce connection details in the "Settings" tab above.
          </div>
        )}

        <div className='row'>
          <div className='col-md-3 col-lg-2'>
            <div className='mb-2'>
              <b>Categories</b>
            </div>
            <ul className='list-group'>
              {flattenedCategories
                .filter((category) => category.wooCommerceCategoryId)
                .map((category) => {
                  return (
                    <li
                      key={category.id}
                      className={
                        `list-group-item ` +
                        (category.id === this.state.selectedCategoryId ? 'active' : '')
                      }
                      onClick={() =>
                        this.setState({ selectedCategoryId: category.id }, () =>
                          this.loadListings(),
                        )
                      }
                      style={{ cursor: 'pointer' }}
                    >
                      {getCategoryName(category)}
                    </li>
                  )
                })}
            </ul>
          </div>

          <div className='col-md-9 col-lg-10'>
            <div>
              {company.wooCommerceUrl ? (
                <div>
                  <BlazeButton
                    buttonProps={{
                      className: 'mr-2 text-muted',
                      type: 'default',
                      size: 'large',
                      onClick: () => {
                        this.syncAllProducts()
                      },
                    }}
                    titleProps={{
                      title: 'Sync all products',
                    }}
                    iconProps={{
                      icon: 'sync',
                    }}
                  />
                  <BlazeButton
                    buttonProps={{
                      className: 'mr-2 text-muted',
                      type: 'default',
                      size: 'large',
                      onClick: () => {
                        if (
                          window.confirm(
                            'Only the products shown below will be synced. Please stay on this page until the sync is complete.',
                          )
                        ) {
                          this.syncAllInventory()
                        }
                      },
                    }}
                    titleProps={{
                      title: 'Sync all inventory',
                    }}
                    iconProps={{
                      icon: 'sync',
                    }}
                  />
                  <a href={company.wooCommerceUrl} target='_blank' rel='noreferrer'>
                    <BlazeButton
                      buttonProps={{
                        className: 'mr-2',
                        type: 'default',
                        size: 'large',
                      }}
                      titleProps={{
                        title: 'Go to your store >',
                      }}
                    />
                  </a>
                </div>
              ) : undefined}
            </div>

            <br />

            {productsSection}
          </div>
        </div>
      </div>
    )
  }
}
